import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { branchesAtom, selectedBranchAtom } from 'atoms/branch';
import { Branch } from 'interfaces/branch';

type BranchSelectorVariant = 'header' | 'inline' | 'mobile';

const SelectorButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'selectorVariant',
})<{ selectorVariant: BranchSelectorVariant }>(({ theme, selectorVariant }) => ({
  borderRadius: 24,
  padding: '6px 16px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  ...(selectorVariant === 'header'
    ? {
        color: '#fff',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        border: '1px solid #fff',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' },
      }
    : selectorVariant === 'mobile'
      ? {
          color: '#fff',
          backgroundColor: theme.palette.primary.main,
          border: 'none',
          maxWidth: '100%',
          minWidth: 0,
          justifyContent: 'space-between',
          overflow: 'hidden',
          '&:hover': { backgroundColor: theme.palette.primary.dark },
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            flexShrink: 0,
          },
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: '#6358E61A',
          border: '1px solid #6358E64D',
          '&:hover': { backgroundColor: '#6358E629' },
        }),
}));

const BranchMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 2),
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
  },
}));

interface BranchSelectorProps {
  variant?: BranchSelectorVariant;
}

const BranchSelector = ({ variant = 'header' }: BranchSelectorProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [pendingBranch, setPendingBranch] = React.useState<Branch | null>(null);
  const open = Boolean(anchorEl);
  const branches = useAtomValue(branchesAtom);
  const [selectedBranch, setSelectedBranch] = useAtom(selectedBranchAtom);
  const { t } = useTranslation();

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (variant === 'mobile') {
      setPendingBranch(selectedBranch);
      setDialogOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setPendingBranch(null);
  };

  const handleSelect = (branch: Branch) => {
    if (variant === 'mobile') {
      setPendingBranch(branch);
    } else {
      setSelectedBranch(branch);
      handleClose();
    }
  };

  const handleSave = () => {
    if (pendingBranch) {
      setSelectedBranch(pendingBranch);
    }
    handleDialogClose();
  };

  const hasMultipleBranches = branches.length > 1;

  const endIcon = !hasMultipleBranches ? undefined : variant === 'mobile' ? <AddIcon /> : <KeyboardArrowDown />;

  if (!selectedBranch) {
    return null;
  }

  return (
    <>
      <SelectorButton
        selectorVariant={variant}
        id="branch-selector-button"
        aria-controls={open ? 'branch-selector-menu' : undefined}
        aria-haspopup={hasMultipleBranches ? 'true' : undefined}
        aria-expanded={open ? 'true' : undefined}
        onClick={hasMultipleBranches ? handleOpen : undefined}
        startIcon={<StorefrontOutlinedIcon />}
        endIcon={endIcon}
        sx={!hasMultipleBranches ? { cursor: 'default', '&:hover': { backgroundColor: 'inherit' } } : undefined}
        disableRipple={!hasMultipleBranches}
      >
        <Box
          textAlign="left"
          display="flex"
          flexDirection="column"
          gap={0}
          minWidth={0}
          sx={{ flex: variant === 'mobile' ? '1 1 auto' : '0 0 auto', overflow: 'hidden' }}
        >
          <Typography variant="body2" fontWeight="bold" lineHeight={1.2} display="block" noWrap>
            {selectedBranch.name}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }} lineHeight={1.2} display="block" noWrap>
            {selectedBranch.address?.title}
          </Typography>
        </Box>
      </SelectorButton>

      {!hasMultipleBranches ? null : variant === 'mobile' ? (
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="sm"
          slotProps={{
            paper: {
              sx: {
                borderRadius: '2px',
                m: 2,
                maxHeight: '80vh',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2, pb: 0.5 }}>
            <Typography variant="subtitle1">{t('branchSelector.selectBranchTitle')}</Typography>
            <IconButton onClick={handleDialogClose} size="small" aria-label={t('common.close')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <DialogContent sx={{ px: 0, pt: 0, pb: 0 }}>
            <List disablePadding>
              {branches.map(branch => {
                const isSelected = branch.id === (pendingBranch ?? selectedBranch).id;
                return (
                  <ListItemButton
                    key={branch.id}
                    selected={isSelected}
                    onClick={() => handleSelect(branch)}
                    sx={{
                      borderRadius: 0,
                      py: 0.8,
                      px: 2,
                      '&.Mui-selected': {
                        backgroundColor: '#EDE7F6',
                        '&:hover': { backgroundColor: '#E1D5F5' },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, alignSelf: 'center' }}>
                      <StorefrontOutlinedIcon color="action" sx={{ fontSize: '1.1rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      sx={{ my: 0, '& .MuiListItemText-secondary': { mt: '-2px' } }}
                      primary={
                        <Typography variant="caption" fontWeight="bold" display="block" fontSize="0.75rem" lineHeight={0.9}>
                          {branch.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" fontSize="0.6rem" lineHeight={0.9}>
                          {branch.address?.title}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </DialogContent>
          <Box sx={{ px: 2, pb: 2, pt: 1.5 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSave}
              sx={{ borderRadius: 2, py: 1, textTransform: 'none', fontWeight: 'bold' }}
            >
              {t('branchSelector.save')}
            </Button>
          </Box>
        </Dialog>
      ) : (
        <Menu
          id="branch-selector-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            list: {
              'aria-labelledby': 'branch-selector-button',
            },
            paper: {
              sx: { minWidth: 240, mt: 1 },
            },
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
            {t('branchSelector.selectBranchLabel')}
          </Typography>
          {branches.map(branch => (
            <BranchMenuItem key={branch.id} selected={branch.id === selectedBranch.id} onClick={() => handleSelect(branch)}>
              <StorefrontOutlinedIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="body2" fontWeight="bold" lineHeight={1.3}>
                  {branch.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1}>
                  {branch.address?.title}
                </Typography>
              </Box>
            </BranchMenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

export default BranchSelector;
